"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.isSupportedEventType = exports.SupportedAnalyticsEvent = void 0;
const constants_1 = require("@formsort/constants");
const iframe_utils_1 = require("./iframe-utils");
const typeGuards_1 = require("./typeGuards");
const utils_1 = require("./utils");
const DEFAULT_FLOW_ORIGIN = `https://flow.formsort.com`;
const DEFAULT_CONFIG = {
    useHistoryAPI: false,
    origin: DEFAULT_FLOW_ORIGIN,
};
var SupportedAnalyticsEvent;
(function (SupportedAnalyticsEvent) {
    SupportedAnalyticsEvent["FlowLoaded"] = "FlowLoaded";
    SupportedAnalyticsEvent["FlowClosed"] = "FlowClosed";
    SupportedAnalyticsEvent["FlowFinalized"] = "FlowFinalized";
    SupportedAnalyticsEvent["StepLoaded"] = "StepLoaded";
    SupportedAnalyticsEvent["StepCompleted"] = "StepCompleted";
})(SupportedAnalyticsEvent = exports.SupportedAnalyticsEvent || (exports.SupportedAnalyticsEvent = {}));
const isSupportedEventType = (eventType) => eventType in SupportedAnalyticsEvent;
exports.isSupportedEventType = isSupportedEventType;
const FormsortWebEmbed = (rootEl, config = DEFAULT_CONFIG) => {
    const iframeEl = document.createElement('iframe');
    const { style, autoHeight } = config;
    const formsortOrigin = config.origin || DEFAULT_FLOW_ORIGIN;
    iframeEl.style.border = 'none';
    if (style) {
        const { width = '', height = '' } = style;
        iframeEl.style.width = width;
        iframeEl.style.height = height;
    }
    rootEl.appendChild(iframeEl);
    const sendMessage = (0, iframe_utils_1.getMessageSender)(iframeEl);
    const eventListenersArrayMap = {
        [SupportedAnalyticsEvent.FlowLoaded]: [],
        [SupportedAnalyticsEvent.FlowClosed]: [],
        [SupportedAnalyticsEvent.FlowFinalized]: [],
        [SupportedAnalyticsEvent.StepLoaded]: [],
        [SupportedAnalyticsEvent.StepCompleted]: [],
        redirect: [],
        unauthorized: [],
    };
    const onStyleSetRequest = () => {
        sendMessage({
            type: constants_1.WebEmbedMessage.EMBED_STYLE_SET_RESPONSE_MSG,
            payload: { styleSet: config.styleSet },
        });
    };
    const onTokenRequest = (data) => {
        var _a;
        const { payload } = data;
        if (payload === constants_1.TokenRequestPayload.ID) {
            if ((_a = config.authentication) === null || _a === void 0 ? void 0 : _a.idToken) {
                sendMessage({
                    type: constants_1.WebEmbedMessage.EMBED_TOKEN_RESPONSE_MSG,
                    payload: { token: config.authentication.idToken },
                });
            }
            else {
                throw new Error(`The loaded Flow requires authentication using an ID token, please provide it in config.authentication.idToken.`);
            }
        }
    };
    const onRedirectMessage = (redirectData) => {
        const { payload: url, answers } = redirectData;
        if (!(0, utils_1.isEmpty)(eventListenersArrayMap.redirect)) {
            let cancelRedirect = false;
            // Cancel redirect if any of the redirect listeners return `{ cancel: true }`
            for (const redirectListener of eventListenersArrayMap.redirect) {
                const { cancel } = redirectListener({ url, answers }) || {};
                if (!cancelRedirect && cancel) {
                    cancelRedirect = true;
                }
            }
            if (cancelRedirect) {
                return;
            }
        }
        if (config.useHistoryAPI &&
            'history' in window &&
            url.indexOf(window.location.origin) === 0) {
            window.history.pushState({}, document.title, url);
        }
        else {
            window.location.assign(url);
        }
    };
    const onUnauthorizedMessage = () => {
        if (!(0, utils_1.isEmpty)(eventListenersArrayMap.unauthorized)) {
            for (const unathorizedListener of eventListenersArrayMap.unauthorized) {
                unathorizedListener();
            }
        }
    };
    const onResizeMessage = (data) => {
        const { width, height } = data.payload;
        setSize(width, height);
    };
    // @TODO: In Typescript v4+ MessageEvent is generic
    // and can be typed as MessageEvent<unknown> to increase type safety.
    const onWindowMessage = (message) => {
        const { origin: msgOrigin, source, data } = message;
        if (source !== iframeEl.contentWindow) {
            // If we have multiple Formsort instances within a page, only listen to events coming
            // from the iframe that this embed instance controls.
            return;
        }
        if (msgOrigin !== formsortOrigin) {
            return;
        }
        if (!(0, typeGuards_1.isIWebEmbedEventData)(data)) {
            return;
        }
        if ((0, typeGuards_1.isIFrameAnalyticsEventData)(data)) {
            onEventMessage(data);
        }
        else if ((0, typeGuards_1.isIFrameTokenRequestEventData)(data)) {
            onTokenRequest(data);
        }
        else if ((0, typeGuards_1.isIFrameRedirectEventData)(data)) {
            onRedirectMessage(data);
        }
        else if ((0, typeGuards_1.isIFrameResizeEventData)(data) && autoHeight) {
            onResizeMessage(data);
        }
        else if ((0, typeGuards_1.isIFrameUnauthorizedEventData)(data)) {
            onUnauthorizedMessage();
        }
        else if ((0, typeGuards_1.isIFrameStyleSetRequestEventData)(data)) {
            onStyleSetRequest();
        }
    };
    if (typeof window !== undefined) {
        window.addEventListener('message', onWindowMessage);
    }
    const setSize = (width, height) => {
        if (width !== undefined) {
            iframeEl.style.width = width.toString();
        }
        if (height !== undefined) {
            iframeEl.style.height = height.toString();
        }
    };
    const getEventListenerArray = (eventType) => {
        if ((0, exports.isSupportedEventType)(eventType)) {
            return eventListenersArrayMap[eventType];
        }
        return undefined;
    };
    const onEventMessage = (eventData) => {
        const { eventType, answers } = eventData;
        if (eventType === constants_1.AnalyticsEventType.FlowClosed) {
            removeListeners();
            rootEl.removeChild(iframeEl);
        }
        const eventListenersArr = getEventListenerArray(eventType);
        if (!eventListenersArr) {
            return;
        }
        for (const eventListener of eventListenersArr) {
            eventListener({ answers });
        }
    };
    const removeListeners = () => {
        window.removeEventListener('message', onWindowMessage);
    };
    const loadFlow = (clientLabel, flowLabel, variantLabel, queryParams) => {
        let url = `${formsortOrigin}/client/${clientLabel}/flow/${flowLabel}`;
        if (variantLabel) {
            url += `/variant/${variantLabel}`;
        }
        if (queryParams) {
            url += `?${queryParams
                .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
                .join('&')}`;
        }
        iframeEl.src = url;
    };
    const unloadFlow = () => {
        removeListeners();
        rootEl.removeChild(iframeEl);
    };
    return {
        loadFlow,
        unloadFlow,
        setSize,
        addEventListener(eventName, fn) {
            (0, utils_1.addToArrayMap)(eventListenersArrayMap, eventName, fn);
        },
        removeEventListener(eventName, eventListener) {
            (0, utils_1.removeFromArrayMap)(eventListenersArrayMap, eventName, eventListener);
        },
    };
};
exports.default = FormsortWebEmbed;
